/**
 * Metadata utilities for OpenGraph tags, Twitter Cards, and JSON-LD structured data.
 *
 * These functions help generate appropriate meta tags for SEO and social sharing
 * across different route types in the NFT marketplace.
 */

import { StructuredData } from '../types/global'

/** Base OpenGraph image URL - used as fallback or for constructing full URLs */
export const BASE_OG_IMAGE = 'https://stealthisnft.xyz/og-image.png'

/** Default site information for metadata */
export const SITE_INFO = {
  title: 'VIRI NFT Marketplace',
  description: 'NFT marketplace and utility for the Songbird Network',
  url: 'https://stealthisnft.xyz',
  image: BASE_OG_IMAGE,
  twitterHandle: '@stealthisnft',
}

/**
 * Generates OpenGraph meta tags object for react-helmet
 *
 * @param options - Configuration for OpenGraph tags
 * @returns Object with name/content pairs for meta tags
 */
export function createOpenGraphMeta(options: {
  /** Page-specific title (will be combined with site title) */
  title?: string
  /** Full URL to the page */
  url?: string
  /** OG type: website, article, profile, product, etc. */
  type?: 'website' | 'article' | 'profile' | 'product'
  /** Description for the page */
  description?: string
  /** Image URL for social sharing */
  image?: string
  /** Image alt text */
  imageAlt?: string
  /** For articles: published time */
  publishedTime?: string
  /** For articles: modified time */
  modifiedTime?: string
  /** For profiles: username */
  profileUsername?: string
  /** For products: price, currency, availability */
  price?: string
  currency?: string
  availability?: string
}): Array<{ property: string; content: string }> {
  const meta: Array<{ property: string; content: string }> = []

  const fullTitle = options.title
    ? `${options.title} | ${SITE_INFO.title}`
    : SITE_INFO.title

  meta.push(
    { property: 'og:title', content: fullTitle },
    { property: 'og:description', content: options.description || SITE_INFO.description },
    { property: 'og:url', content: options.url || SITE_INFO.url },
    { property: 'og:type', content: options.type || 'website' },
    { property: 'og:image', content: options.image || SITE_INFO.image },
    { property: 'og:image:alt', content: options.imageAlt || fullTitle },
    { property: 'og:site_name', content: SITE_INFO.title }
  )

  // Optional product-specific tags
  if (options.type === 'product' && options.price) {
    meta.push({ property: 'product:price:amount', content: options.price })
    if (options.currency) {
      meta.push({ property: 'product:price:currency', content: options.currency })
    }
    if (options.availability) {
      meta.push({ property: 'product:availability', content: options.availability })
    }
  }

  return meta
}

/**
 * Generates Twitter Card meta tags for react-helmet
 *
 * @param options - Configuration for Twitter Card
 * @returns Array of meta tag objects
 */
export function createTwitterCardMeta(options: {
  /** Card type: summary, summary_large_image, player, app */
  card?: 'summary' | 'summary_large_image' | 'player' | 'app'
  title?: string
  description?: string
  image?: string
  imageAlt?: string
  /** Twitter username without @ */
  site?: string
  /** Creator username without @ */
  creator?: string
}): Array<{ name: string; content: string }> {
  const meta: Array<{ name: string; content: string }> = []

  const fullTitle = options.title
    ? `${options.title} | ${SITE_INFO.title}`
    : SITE_INFO.title

  meta.push(
    { name: 'twitter:card', content: options.card || 'summary_large_image' },
    { name: 'twitter:title', content: fullTitle },
    { name: 'twitter:description', content: options.description || SITE_INFO.description },
    { name: 'twitter:image', content: options.image || SITE_INFO.image },
    { name: 'twitter:image:alt', content: options.imageAlt || fullTitle },
    { name: 'twitter:site', content: options.site || SITE_INFO.twitterHandle.replace('@', '') }
  )

  if (options.creator) {
    meta.push({ name: 'twitter:creator', content: options.creator })
  }

  return meta
}

/**
 * Creates JSON-LD structured data for an NFT item/product
 *
 * @param options - NFT item data
 * @returns JSON-LD structured data object
 */
export function createNftProductSchema(options: {
  /** NFT token name */
  name: string
  /** NFT token ID */
  tokenId: string
  /** Collection/contract name */
  collectionName: string
  /** Full URL to the NFT page */
  url: string
  /** Image URL of the NFT */
  image: string
  /** Description of the NFT */
  description?: string
  /** Current price in native token (e.g., "1.5") */
  price?: string
  /** Currency symbol (e.g., "SGB", "ETH") */
  currency?: string
  /** Blockchain network name */
  chain: string
  /** Contract address */
  contractAddress: string
  /** Creator address */
  creator?: string
  /** Owner address */
  owner?: string
  /** Attributes as key-value pairs */
  attributes?: Array<{ trait_type: string; value: string | number }>
}): StructuredData {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    name: options.name,
    description: options.description || `${options.collectionName} #${options.tokenId}`,
    image: options.image,
    url: options.url,
    category: 'NFT',
    brand: {
      '@type': 'Brand',
      name: options.collectionName,
    },
    offers: {
      '@type': 'Offer',
      price: options.price || '0',
      priceCurrency: options.currency || 'SGB',
      availability: 'https://schema.org/InStock',
      seller: {
        '@type': 'Organization',
        name: 'VIRI Marketplace',
        url: 'https://stealthisnft.xyz',
      },
    },
    additionalProperty: [],
  }

  // Add blockchain-specific properties
  if (options.chain) {
    schema.additionalProperty?.push({
      '@type': 'PropertyValue',
      name: 'blockchain',
      value: options.chain,
    })
  }

  if (options.contractAddress) {
    schema.additionalProperty?.push({
      '@type': 'PropertyValue',
      name: 'contractAddress',
      value: options.contractAddress,
    })
  }

  if (options.tokenId) {
    schema.additionalProperty?.push({
      '@type': 'PropertyValue',
      name: 'tokenId',
      value: options.tokenId,
    })
  }

  // Add NFT attributes as properties
  if (options.attributes && options.attributes.length > 0) {
    schema.additionalProperty = schema.additionalProperty || []
    options.attributes.forEach((attr) => {
      schema.additionalProperty?.push({
        '@type': 'PropertyValue',
        name: attr.trait_type,
        value: attr.value,
      })
    })
  }

  return schema
}

/**
 * Creates JSON-LD structured data for a collection
 *
 * @param options - Collection data
 * @returns JSON-LD structured data object
 */
export function createCollectionSchema(options: {
  /** Collection name */
  name: string
  /** Collection description */
  description: string
  /** Full URL to the collection page */
  url: string
  /** Banner image URL */
  image?: string
  /** Collection creator/owner */
  creator?: string
  /** Number of NFTs in collection */
  count?: number
  /** Blockchain network */
  chain?: string
  /** Contract address */
  contractAddress?: string
}): StructuredData {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'CollectionPage',
    name: options.name,
    description: options.description,
    url: options.url,
  }

  if (options.image) {
    schema.image = options.image
  }

  if (options.creator) {
    schema.creator = {
      '@type': 'Organization',
      name: options.creator,
    }
  }

  // Add NFT collection specific properties
  schema.additionalProperty = []

  if (options.count !== undefined) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'itemCount',
      value: options.count,
    })
  }

  if (options.chain) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'blockchain',
      value: options.chain,
    })
  }

  if (options.contractAddress) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'contractAddress',
      value: options.contractAddress,
    })
  }

  return schema
}

/**
 * Creates JSON-LD structured data for a user profile
 *
 * @param options - User profile data
 * @returns JSON-LD structured data object
 */
export function createProfileSchema(options: {
  /** User display name */
  name: string
  /** User bio/description */
  bio?: string
  /** Full URL to profile page */
  url: string
  /** Profile image URL */
  image?: string
  /** Wallet address */
  address?: string
  /** Number of NFTs owned */
  ownedCount?: number
  /** Number of NFTs created */
  createdCount?: number
}): StructuredData {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'ProfilePage',
    name: options.name,
    url: options.url,
  }

  if (options.bio) {
    schema.description = options.bio
  }

  if (options.image) {
    schema.image = options.image
  }

  // Add custom properties
  schema.additionalProperty = []

  if (options.address) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'walletAddress',
      value: options.address,
    })
  }

  if (options.ownedCount !== undefined) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'ownedNFTs',
      value: options.ownedCount,
    })
  }

  if (options.createdCount !== undefined) {
    schema.additionalProperty.push({
      '@type': 'PropertyValue',
      name: 'createdNFTs',
      value: options.createdCount,
    })
  }

  return schema
}

/**
 * Creates JSON-LD structured data for the homepage (list of items/auctions)
 *
 * @param options - Homepage data
 * @returns JSON-LD structured data object
 */
export function createItemListSchema(options: {
  /** Title for the list */
  title: string
  /** Description */
  description: string
  /** URL of the page */
  url: string
  /** Array of items/products in the list */
  items: Array<{
    name: string
    description?: string
    image: string
    url: string
    price?: string
    currency?: string
  }>
}): StructuredData {
  const schema: StructuredData = {
    '@context': 'https://schema.org/',
    '@type': 'ItemList',
    name: options.title,
    description: options.description,
    url: options.url,
    numberOfItems: options.items.length,
    itemListElement: options.items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      item: {
        '@type': 'Product',
        name: item.name,
        description: item.description,
        image: item.image,
        url: item.url,
        ...(item.price && {
          offers: {
            '@type': 'Offer',
            price: item.price,
            priceCurrency: item.currency || 'SGB',
          },
        }),
      },
    })),
  }

  return schema
}

/**
 * Creates JSON-LD structured data for a breadcrumb trail.
 *
 * @param options - Breadcrumb data
 * @returns JSON-LD structured data object
 */
export function createBreadcrumbListSchema(options: {
  /** Full page URL for the current page */
  url: string
  /** Ordered breadcrumb segments from root to current page */
  segments: Array<{
    /** Display name for the breadcrumb segment */
    name: string
    /** Full URL for the segment; defaults to site root when omitted */
    url?: string
  }>
}): StructuredData {
  const items = options.segments.map((segment, index) => ({
    '@type': 'ListItem',
    position: index + 1,
    name: segment.name,
    item: segment.url || 'https://stealthisnft.xyz',
  }))

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items,
  }
}

/**
 * Creates a WebSite structured data schema for the homepage.
 *
 * Provides search engine bots with information about the website itself,
 * including name, URL, description, and optional search action.
 *
 * @param options - Configuration for the WebSite schema
 * @returns StructuredData object for JSON-LD
 *
 * @example
 * ```typescript
 * const schema = createWebSiteSchema({
 *   name: 'VIRI NFT Marketplace',
 *   url: 'https://stealthisnft.xyz',
 *   description: 'NFT marketplace on Songbird Network',
 * })
 * ```
 */
export function createWebSiteSchema(options: {
  name: string
  url: string
  description?: string
  logo?: string
  sameAs?: string[] // Social media URLs
}): StructuredData {
  const { name, url, description, logo, sameAs } = options

  const schema: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name,
    url,
    ...(description && { description }),
    ...(logo && { image: logo }),
    ...(sameAs && sameAs.length > 0 && { sameAs }),
    // Optional: Add search action if you have a search page
    potentialAction: {
      '@type': 'SearchAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: 'https://stealthisnft.xyz/search?q={search_term_string}',
      },
      'query-input': 'required name=search_term_string',
    },
  }

  return schema
}

/**
 * Creates an Organization structured data schema.
 *
 * Describes the organization behind the website (VIRI).
 * Useful for knowledge panels and entity recognition.
 *
 * @param options - Configuration for the Organization schema
 * @returns StructuredData object for JSON-LD
 */
export function createOrganizationSchema(options: {
  name: string
  url: string
  logo: string
  description?: string
  sameAs?: string[] // Social media URLs
  contactPoint?: {
    telephone?: string
    email?: string
    contactType: string // e.g., 'customer service'
  }
}): StructuredData {
  const { name, url, logo, description, sameAs, contactPoint } = options

  const schema: StructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name,
    url,
    logo,
    ...(description && { description }),
    ...(sameAs && sameAs.length > 0 && { sameAs }),
    ...(contactPoint && { contactPoint: [contactPoint] }),
  }

  return schema
}

/**
 * Creates JSON-LD structured data for a blockchain network reference page.
 *
 * Describes the chain as a schema.org `TechArticle` (the closest fit for a
 * technical reference page) whose `about` entity is a `Blockchain` node
 * carrying the live chain facts, wrapped in an `@graph` alongside a
 * BreadcrumbList so one script tag covers the whole page.
 *
 * @param options - Chain reference data
 * @returns JSON-LD structured data object
 */
export function createChainReferenceSchema(options: {
  /** Display name of the chain, e.g. "Flare" */
  name: string
  /**
   * Full headline for the article node. Pass the same string used for the page
   * title so the headline never reads "XDC Network Network".
   */
  headline?: string
  /** Canonical page URL for this chain */
  url: string
  /** One-line description of the chain */
  description: string
  /** CAIP-2 chain identifier, e.g. "eip155:14" */
  chainId?: string
  /** Native currency ticker, e.g. "FLR" */
  nativeCurrency?: string
  /** Block explorer URL */
  explorerUrl?: string
  /** Consensus mechanism summary */
  consensus?: string
  /** True when the chain is a testnet */
  testnet?: boolean
  /** Breadcrumb trail, root first */
  breadcrumbs: Array<{ name: string; url: string }>
}): StructuredData {
  const {
    name,
    headline,
    url,
    description,
    chainId,
    nativeCurrency,
    explorerUrl,
    consensus,
    testnet,
    breadcrumbs,
  } = options

  const articleHeadline = headline ?? `${name} Network`

  const blockchain: Record<string, unknown> = {
    '@type': 'Blockchain',
    name,
    description,
    ...(chainId && { identifier: chainId, chainId }),
    ...(nativeCurrency && {
      nativeCurrency: { '@type': 'Currency', name: nativeCurrency, tickerSymbol: nativeCurrency },
    }),
    ...(consensus && { consensus }),
    ...(testnet && { isTestNet: true }),
    ...(explorerUrl && { url: explorerUrl }),
  }

  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'TechArticle',
        headline: articleHeadline,
        description,
        url,
        ...(explorerUrl && { image: explorerUrl }),
        about: blockchain,
        isPartOf: {
          '@type': 'WebSite',
          name: SITE_INFO.title,
          url: SITE_INFO.url,
        },
      },
      createBreadcrumbListSchema({ url, segments: breadcrumbs }),
    ],
  }
}

/**
 * Default fallback metadata for any page
 */
export function getDefaultMetadata(title?: string) {
  const fullTitle = title ? `${title} | VIRI` : 'VIRI NFT Marketplace'
  return {
    title: fullTitle,
    description: SITE_INFO.description,
    openGraph: createOpenGraphMeta({ title, type: 'website' }),
    twitterCard: createTwitterCardMeta({ title }),
  }
}
